import {FC, useCallback} from "react";
import {useTranslation} from "react-i18next";
import {useSidebarItems} from "../../hooks";
import {useParams} from "react-router-dom";
import {useQuery} from "@apollo/client";
import {EntityResponse, LookupOldCommonOrgEntity} from "../backend";
import {OldCommonOrgEntity} from "../model";
import {Endpoints} from "../../runtime";
import {ErrorPage, PageHeading, PageTemplate} from "../../layout";
import {ActivityIndicator} from "../../activity-indicator";
import {BackendError} from "@common-core/runtime-js/backend";
import {OldCommonOrgEntityProvider} from "../context";
import {Protected} from "../../access";
import {useAnalytics} from "@common-core/react-analytics";
import {HashTabs} from "../../hash-tabs";

import {OldClientEntityHierarchyTab} from "../hierarchy";
import {OldOverviewTab} from "../old-common-org/overview-tab";
import {CreateEntityButton} from "../operational-hierarchy/create";

interface RouteParams {
  id: string;
}

export const OldClientEntityPage: FC = () => {
  const {t} = useTranslation();
  const {productEvent} = useAnalytics();
  const {id} = useParams<RouteParams>();
  const sidebarItems = useSidebarItems();
  const {loading, error, data} = useQuery<EntityResponse<OldCommonOrgEntity>>(
    LookupOldCommonOrgEntity,
    {
      variables: {id},
      context: {
        endpoint: Endpoints.BACKEND
      }
    }
  );
  const translate = useCallback(
    (tab: string) => {
      return tab === "hierarchy" || tab === "overview"
        ? t(`entity-detail.${tab}`, {lng: "en"})
        : t(`entity-tabs.embedded-components.${tab}`, {lng: "en"});
    },
    [t]
  );
  const entity = data?.entity || ({} as OldCommonOrgEntity);

  if (error) {
    return <ErrorPage error={error} logo={false} sidebarItems={sidebarItems} />;
  }
  if (loading) {
    return (
      <PageTemplate
        id={"v1-client-entity-page"}
        title={"Client Entity Page"}
        sidebarItems={sidebarItems}>
        <ActivityIndicator />
      </PageTemplate>
    );
  }
  if (!loading && data?.entity === undefined) {
    return (
      <ErrorPage
        error={new BackendError("Entity not found", 404)}
        logo={false}
        sidebarItems={sidebarItems}
      />
    );
  }
  return (
    <OldCommonOrgEntityProvider entity={entity}>
      <PageTemplate
        id={"old-client-entity-page"}
        title={"Client Entity Page"}
        sidebarItems={sidebarItems}
        banner={
          <PageHeading
            title={t("client-page.banner.title")}
            summary={t("client-page.banner.summary")}
          />
        }>
        <div
          className={"entity-detail"}
          data-testid={"old-client-entity-detail"}>
          <header className={"entity-detail-header"}>
            <h2 className={"entity-business-name"}>
              {entity.dealerInformation.dbaName}
            </h2>
            <Protected
              permissions={[
                {resource: "operational-entity", action: "create"}
              ]}>
              <CreateEntityButton />
            </Protected>
          </header>
          <HashTabs
            id={"entity-detail-tabs"}
            initial={"overview"}
            onTabActivated={event => {
              productEvent({
                name: `entity_detail_tab_clicked`,
                properties: {
                  location: "Dealer Details Overview",
                  value: `${translate(event.tab)}`,
                  result: "Tab Displayed"
                }
              });
            }}>
            <OldOverviewTab
              hash={"overview"}
              label={t("entity-tabs.client-overview").toString()}
            />
            <OldClientEntityHierarchyTab
              hash={"hierarchy"}
              label={t("entity-tabs.hierarchy")}
            />
          </HashTabs>
        </div>
      </PageTemplate>
    </OldCommonOrgEntityProvider>
  );
};
